import {
  ALL_CANDIDATES_MASK,
  BOARD_SIZE,
  boardFromValues,
  conflictingCells,
} from '../../game/board';
import { countSolutions } from '../../game/solver';
import {
  displayedElapsedMs,
  restoreLegacySession,
  restoreSession,
  type GameSession,
  type SessionStatus,
} from '../../game/session';
import type { Difficulty } from '../../game/generator';

export const SESSION_STORAGE_KEY = 'sudoku.session.v1';
const CURRENT_SESSION_VERSION = 2;

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;
type CommonStoredSession = Record<string, unknown> & {
  difficulty: Difficulty;
  puzzle: number[];
  entries: number[];
  candidates: number[];
  selectedIndex: number;
  elapsedMs: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDifficulty(value: unknown): value is Difficulty {
  return value === 'easy' || value === 'medium' || value === 'hard';
}

function isSessionStatus(value: unknown): value is SessionStatus {
  return value === 'playing' || value === 'paused' || value === 'completed' || value === 'failed';
}

function isNumberArray(value: unknown, predicate: (item: number) => boolean): value is number[] {
  return (
    Array.isArray(value) &&
    value.length === BOARD_SIZE &&
    value.every((item: unknown) => typeof item === 'number' && predicate(item))
  );
}

function hasCommonSessionShape(value: Record<string, unknown>): value is CommonStoredSession {
  return (
    isDifficulty(value.difficulty) &&
    isNumberArray(value.puzzle, (item) => Number.isInteger(item) && item >= 0 && item <= 9) &&
    isNumberArray(value.entries, (item) => Number.isInteger(item) && item >= 0 && item <= 9) &&
    isNumberArray(
      value.candidates,
      (item) =>
        Number.isInteger(item) && item >= 0 && item <= ALL_CANDIDATES_MASK && (item & 1) === 0,
    ) &&
    typeof value.selectedIndex === 'number' &&
    Number.isInteger(value.selectedIndex) &&
    value.selectedIndex >= 0 &&
    value.selectedIndex < BOARD_SIZE &&
    typeof value.elapsedMs === 'number' &&
    Number.isFinite(value.elapsedMs) &&
    value.elapsedMs >= 0
  );
}

function hasValidBoardState(
  puzzle: readonly number[],
  entries: readonly number[],
  candidates: readonly number[],
): boolean {
  const parsedPuzzle = boardFromValues(puzzle);
  return (
    conflictingCells(parsedPuzzle).size === 0 &&
    countSolutions(parsedPuzzle, 2) === 1 &&
    parsedPuzzle.every(
      (clue, index) =>
        (clue === 0 || entries[index] === 0) &&
        ((clue === 0 && entries[index] === 0) || candidates[index] === 0),
    )
  );
}

function restoredStatus(status: SessionStatus): 'paused' | 'failed' {
  return status === 'failed' ? 'failed' : 'paused';
}

export function parseStoredSession(serialized: string | null): GameSession | null {
  if (serialized === null) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(serialized);
    if (!isRecord(value) || !hasCommonSessionShape(value)) {
      return null;
    }
    if (!hasValidBoardState(value.puzzle, value.entries, value.candidates)) {
      return null;
    }
    const common = {
      puzzle: boardFromValues(value.puzzle),
      entries: boardFromValues(value.entries),
      candidates: value.candidates,
      selectedIndex: value.selectedIndex,
      difficulty: value.difficulty,
      elapsedMs: value.elapsedMs,
    };
    if (value.version === 1) {
      return restoreLegacySession(common);
    }
    if (
      value.version !== CURRENT_SESSION_VERSION ||
      !isSessionStatus(value.status) ||
      typeof value.mistakes !== 'number' ||
      !Number.isInteger(value.mistakes) ||
      value.mistakes < 0 ||
      value.mistakes > 3 ||
      (value.status === 'failed' && value.mistakes !== 3) ||
      (value.status !== 'failed' && value.mistakes >= 3)
    ) {
      return null;
    }
    return restoreSession(
      {
        ...common,
        mistakes: value.mistakes,
      },
      restoredStatus(value.status),
    );
  } catch {
    return null;
  }
}

export function loadSession(storage: ReadableStorage): GameSession | null {
  try {
    return parseStoredSession(storage.getItem(SESSION_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveSession(
  storage: WritableStorage,
  session: GameSession,
  nowMs: number,
): boolean {
  try {
    storage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({
        version: CURRENT_SESSION_VERSION,
        status: session.status,
        mistakes: session.mistakes,
        difficulty: session.difficulty,
        puzzle: session.puzzle,
        entries: session.entries,
        candidates: session.candidates,
        selectedIndex: session.selectedIndex,
        elapsedMs: displayedElapsedMs(session, nowMs),
      }),
    );
    return true;
  } catch {
    return false;
  }
}
